import { useQuery } from "@tanstack/react-query";
import { fetchProductGroupPrices, type PricedProduct, type ProductGroupPricesResponse } from "../services/pricingService";
import { Product } from "../types/product";

interface UseProductGroupPricesParams {
	configBundleId: string;
	productGroupId: string;
	productKind: string;
	enabled?: boolean;
}

interface TransformedProductData {
	products: Product[];
	pricedProductMap: Map<string, PricedProduct>;
	manufacturers: string[];
	performanceComponentType: string | null;
}

export function useProductGroupPrices({
	configBundleId,
	productGroupId,
	productKind,
	enabled = true,
}: UseProductGroupPricesParams) {
	return useQuery<ProductGroupPricesResponse, Error, TransformedProductData>({
		queryKey: ["productGroupPrices", configBundleId, productGroupId],
		queryFn: () => fetchProductGroupPrices({ configBundleId, productGroupId }),
		enabled,
		staleTime: 5 * 60 * 1000,
		select: (response) => {
			const pricedProductMap = new Map(
				response.products.map((p) => [p.id, p])
			);

			const products: Product[] = response.products.map((pricedProduct) => ({
				materialNumber: pricedProduct.id,
				kind: productKind,
				title: pricedProduct.title || pricedProduct.description,
				meta: [pricedProduct.description2 || "", pricedProduct.manufacturerName || ""].filter(Boolean),
				description: pricedProduct.description,
				imageUrl: pricedProduct.imageUrl,
				stockStatus: pricedProduct.stockStatus,
				priceDifference: pricedProduct.priceDifference,
				manufacturer: pricedProduct.manufacturer,
			}));

			return {
				products,
				pricedProductMap,
				manufacturers: response.manufacturers || [],
				performanceComponentType: response.performanceComponentType ?? null,
			};
		},
	});
}